import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import GphConversationCanvas from './GphConversationCanvas';
import GphAgentInput from './GphAgentInput';
import GphQuickIntents from './GphQuickIntents';
import MiIonicAppShell from './mi-shell/MiIonicAppShell';
import MiOverflowMenu from './mi-shell/MiOverflowMenu';
import MiTopBar from './mi-shell/MiTopBar';
// FullScreenBlockRenderer kept on disk as legacy fallback (?classic=1) but no longer used here.
import { GPH_COPY } from '../../services/gph/gphCopy';
import { getCapability, resolveCalculatorForGame, CAPABILITIES } from '../../services/gph/gphCapabilityRegistry';
import { VIDEO_GOAL_OPTIONS } from '../../services/gph/gphIntentResolver';
import { flowReducer, createInitialFlowState, FLOW_STATES } from '../../services/gph/gphFlowStateMachine';
import {
  understand,
  understandVideoAttachment,
  selectSinglePrimaryOutput
} from '../../services/gph/gphNaturalConversationAgent';
import {
  createContext as createGphContext,
  hydrateContext,
  resetConversationContext,
  updateFromResult,
  updatePlayerMemory,
  serializeContext
} from '../../services/gph/gphContextEngine';
import {
  TURN_KINDS,
  classifyTurn,
  applyContextReset,
  registerVideoAttachment,
  hasRecentVideo,
  resolveVideoObjective
} from '../../services/gph/gphConversationStateManager';
import { evaluateResearch } from '../../services/gph/gphResearchDecision';
import { requestResearch } from '../../services/gph/gphResearchClient';
import { logAgentTurn } from '../../services/gph/gphAgentLog';
import { evaluateAccess, ACCESS_REASONS } from '../../services/gph/gphEntitlementPolicy';
import { registerUse } from '../../services/gph/gphFreeUsagePolicy';
import { runMiSkillTurn } from '../../skills/orchestration/miSkillOrchestrator';
import {
  composeLegacyCapabilityAnswer,
  composeMiSkillAnswer,
  sanitizeMiUserFacingText
} from '../../skills/orchestration/miAnswerComposer';
import {
  loadAgentState,
  saveAgentState,
  appendMessage as persistMessage,
  loadConversationMessages,
  estimateBytes,
  newConversationId,
  checkConversationLimit,
  createDefaultAgentState
} from '../../services/gph/gphConversationStore';
import { sendMiConversationTurn } from '../../services/gph/miConversationClient';
import './GphAgentApp.css';

// Rótulos amigáveis dos próximos passos (valores = capabilityId ou ações especiais).
const NEXT_STEP_LABELS = {
  sensitivity_diagnostic: 'Diagnosticar sensibilidade',
  calculator_auto: 'Calcular outras miras',
  mi_analysis_gameplay: 'Analisar um vídeo',
  mi_analysis_aim: 'Validar com vídeo',
  training_single_drill: 'Gerar treino',
  training_favorites: 'Meus favoritos',
  purchase_pro: 'Conhecer o Pro',
  profile_plan: 'Meu plano',
  profile_settings: 'Configurações',
  support_community: 'Suporte',
  restart: GPH_COPY.startAnotherProblem
};

const GAME_OPTIONS = [
  { label: 'PUBG Mobile', value: 'pubg_mobile' },
  { label: 'Free Fire', value: 'free_fire' },
  { label: 'CODM', value: 'codm' }
];

const nextStepChips = (steps = []) =>
  steps.map((step) => ({ label: NEXT_STEP_LABELS[step] || step, value: step }));

const createWelcomeMessage = () => ({
  id: 'gph_welcome_boot',
  role: 'gph',
  kind: 'text',
  text: GPH_COPY.welcome
});

export default function GphAgentApp({ host }) {
  const [flow, dispatchFlow] = useReducer(flowReducer, undefined, createInitialFlowState);
  const [messages, setMessages] = useState(() => [createWelcomeMessage()]);
  const [agentState, setAgentState] = useState(createDefaultAgentState);
  const [inputBlocked, setInputBlocked] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const conversationIdRef = useRef(null);
  const conversationBytesRef = useRef(0);
  const idCounterRef = useRef(0);
  const thinkTimerRef = useRef(null);
  const agentStateRef = useRef(agentState);
  agentStateRef.current = agentState;

  // Indicador de digitação premium: mostra o typing por um instante curto antes
  // da resposta do GPH montar. Reduced-motion praticamente elimina o atraso.
  const createThinkingState = (message = "Analisando seu pedido...", phase = 'Pensando na resposta') => ({
    message,
    phase,
    startedAt: Date.now()
  });

  const showThinking = (message, phase) => {
    setThinking(createThinkingState(message, phase));
  };

  const respondWith = (fn, message = "Analisando seu pedido...") => {
    const reduce = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (thinkTimerRef.current) clearTimeout(thinkTimerRef.current);
    showThinking(message);
    thinkTimerRef.current = setTimeout(async () => {
      try {
        await fn();
      } catch (error) {
        console.error('[GPH_AGENT] Failed to resolve Mi response', error);
        gphSay(GPH_COPY.errorRecovery);
      } finally {
        setThinking(false);
      }
    }, reduce ? 40 : 580);
  };

  useEffect(() => () => { if (thinkTimerRef.current) clearTimeout(thinkTimerRef.current); }, []);

  const uid = host.user?.uid || null;
  const plan = host.entitlements?.isPro ? 'pro' : 'free';

  // ── Entitlement da Mi (espelho do backend, que é o source of truth) ──────
  // Free tem 1 conversa de degustação na vida da conta; depois, só Pro.
  // O backend bloqueia Gemini/skills; aqui refletimos com paywall sem custo.
  const isProUser = host.entitlements?.isPro === true || host.hasPrivilegedAccess === true;
  const miTrialConsumedRef = useRef(false);
  const miTrialLocked = !isProUser && (
    host.profile?.miFreeTrialLocked === true
    || host.profile?.miFreeTrialUsed === true
    || miTrialConsumedRef.current
  );

  const showMiPaywall = (message) => {
    if (message) gphSay(message);
    appendMessages({
      role: 'gph',
      kind: 'rich',
      adapterKey: 'PurchaseProAdapter',
      capabilityId: 'purchase_pro',
      context: { blockedReason: message || GPH_COPY.proOnlySkill },
      text: 'Assinar Pro'
    });
    transition(FLOW_STATES.PURCHASE_REQUIRED);
  };

  // ── Expert Core: contexto da conversa + memória permanente do jogador ────
  const [conversationCtx, setConversationCtx] = useState(createGphContext);
  const conversationCtxRef = useRef(conversationCtx);
  conversationCtxRef.current = conversationCtx;

  // Toda mudança de contexto persiste no doc coach_state/gph_agent existente
  // (serializeContext nunca inclui tokens, vídeo ou payloads ricos).
  const patchContext = (updater) => {
    setConversationCtx((prev) => {
      const next = updater(prev);
      if (uid) saveAgentState(uid, { gphContext: serializeContext(next), plan });
      return next;
    });
  };

  const planLabel = host.hasAdminPanelAccess
    ? 'ADM'
    : host.isPartnerUser
      ? 'Parceiro'
      : host.entitlements?.isPro ? 'Pro' : 'Free';

  const nextId = () => `m_${Date.now().toString(36)}_${idCounterRef.current++}`;

  const transition = (to, patch) => dispatchFlow({ type: 'TRANSITION', to, patch });

  // ── Mensagens ────────────────────────────────────────────────────────────
  const appendMessages = (records) => {
    const list = Array.isArray(records) ? records : [records];
    const stamped = list.map((record) => ({ ...record, id: record.id || nextId() }));
    setMessages((prev) => [...prev, ...stamped]);

    // Persistência compacta (nunca payloads ricos) + contagem de bytes.
    let addedBytes = 0;
    for (const record of stamped) {
      const compact = {
        role: record.role,
        type: record.kind === 'rich' ? 'rich_result' : (record.kind === 'chips' ? 'text' : record.kind),
        text: record.text || '',
        capabilityId: record.capabilityId || null
      };
      addedBytes += estimateBytes(compact);
      if (uid && conversationIdRef.current && record.kind !== 'rich' && record.persist !== false) {
        persistMessage(uid, conversationIdRef.current, compact);
      }
    }
    conversationBytesRef.current += addedBytes;

    const limitCheck = checkConversationLimit({
      plan,
      conversationBytesUsed: conversationBytesRef.current,
      totalBytesUsed: (agentStateRef.current.totalBytes || 0) + addedBytes
    });
    setAgentState((prev) => ({ ...prev, totalBytes: (prev.totalBytes || 0) + addedBytes }));
    if (limitCheck.blocked && !inputBlocked) {
      setInputBlocked(true);
      const noticeText = plan === 'pro' ? GPH_COPY.conversationLimitPro : GPH_COPY.conversationLimitFree;
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'gph', kind: 'notice', text: noticeText },
        {
          id: nextId(),
          role: 'gph',
          kind: 'chips',
          chipsKind: 'conversation_limit',
          chips: [{ label: GPH_COPY.startNewConversation, value: 'new_conversation' }]
        }
      ]);
    }

    return stamped;
  };

  const gphSay = (text, extra = {}) => appendMessages({ role: 'gph', kind: 'text', text, ...extra });

  const askChips = (text, chips, chipsKind, extra = {}) => {
    // Dock único de próximos passos: expira o dock anterior antes de sugerir
    // um novo, para não acumular botões repetidos ("Começar outro problema",
    // "Diagnosticar sensibilidade") a cada turno da conversa.
    if (chipsKind === 'next_steps') {
      setMessages((prev) => prev.map((message) => (
        message.kind === 'chips' && message.chipsKind === 'next_steps' && !message.resolved
          ? { ...message, resolved: true }
          : message
      )));
    }
    return appendMessages({ role: 'gph', kind: 'chips', text, chips, chipsKind, ...extra });
  };

  const resolveChipMessage = (messageId, value) => {
    setMessages((prev) => prev.map((message) => (
      message.id === messageId ? { ...message, resolved: true, selectedValue: value } : message
    )));
  };

  // ── Boot ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = uid ? await loadAgentState(uid) : createDefaultAgentState();
      if (cancelled) return;
      setAgentState(loaded);
      setConversationCtx(hydrateContext(loaded.gphContext));
      const convId = loaded.activeConversationId || newConversationId();
      conversationIdRef.current = convId;
      conversationBytesRef.current = Number(loaded.conversationBytes?.[convId] || 0);
      const persistedMessages = uid ? await loadConversationMessages(uid, convId) : [];
      if (cancelled) return;
      if (persistedMessages.length) {
        setMessages(persistedMessages);
      }
      if (uid && !loaded.activeConversationId) saveAgentState(uid, { activeConversationId: convId, plan });
    })();
    return () => { cancelled = true; };
    // Boot roda uma única vez por usuário: appendMessages/plan são estáveis aqui.
    // eslint-disable-next-line
  }, [uid]);

  // ── Uso Free / favoritos ─────────────────────────────────────────────────
  const registerUsage = (capabilityId) => {
    setAgentState((prev) => {
      const usage = registerUse(prev.usage, capabilityId);
      if (uid) saveAgentState(uid, { usage, plan });
      return { ...prev, usage };
    });
  };

  const favoritesApi = {
    list: () => agentState.favorites || [],
    isFavorite: (drillId) => (agentState.favorites || []).some((fav) => fav.drillId === drillId),
    toggle: (drill) => {
      setAgentState((prev) => {
        const exists = (prev.favorites || []).some((fav) => fav.drillId === drill.drillId);
        const favorites = exists
          ? prev.favorites.filter((fav) => fav.drillId !== drill.drillId)
          : [...(prev.favorites || []), {
            favoriteId: `fav_${drill.drillId}`,
            drillId: drill.drillId,
            title: drill.title,
            source: 'training_single_drill',
            createdAt: new Date().toISOString()
          }];
        if (uid) saveAgentState(uid, { favorites, plan });
        return { ...prev, favorites };
      });
    }
  };

  // ── Guards + montagem de skill ───────────────────────────────────────────
  const suggestNextSteps = (capabilityId) => {
    // Mi-first: proximos passos devem vir como texto da Mi, nao como botoes
    // repetidos na timeline. Mantemos a funcao como no-op para preservar
    // compatibilidade com fluxos legados que ainda a chamam.
    return null;
  };

  const mountCapability = (capability, context = {}) => {
    transition(FLOW_STATES.MOUNTING_SKILL, { capabilityId: capability.capabilityId, mountedAdapter: capability.adapter });

    if (capability.type === 'skill') {
      appendMessages({
        role: 'gph',
        kind: 'text',
        capabilityId: capability.capabilityId,
        text: composeLegacyCapabilityAnswer({ capabilityId: capability.capabilityId, context })
      });
      transition(FLOW_STATES.SHOWING_RESULT);
      patchContext((prev) => updateFromResult(prev, capability.capabilityId, context));
      logAgentTurn({
        skillChosen: capability.capabilityId,
        slots: context,
        schemaType: 'legacy_skill_answer_composed'
      });
      suggestNextSteps(capability.capabilityId);
      return;
    }

    appendMessages({
      role: 'gph',
      kind: 'rich',
      adapterKey: capability.adapter,
      capabilityId: capability.capabilityId,
      context,
      text: capability.friendlyName
    });
    transition(FLOW_STATES.SHOWING_RESULT);

    // Context Engine: registra o resultado para as próximas ações contextuais
    // ("treinar essa situação", "validar com vídeo").
    patchContext((prev) => updateFromResult(prev, capability.capabilityId, context));
    logAgentTurn({ skillChosen: capability.capabilityId, slots: context, schemaType: capability.adapter });

    // Blocos com fluxo próprio (full-screen ou ações internas) sugerem próximos
    // passos ao concluir; calculadoras inline recebem o dock imediatamente.
    if (capability.adapter === 'CalculatorSkillAdapter') {
      suggestNextSteps(capability.capabilityId);
    }
  };

  const guardAndMount = (capabilityId, context = {}) => {
    let capability = getCapability(capabilityId);
    if (!capability) return;

    // CODM: skill indisponível — nunca oferecer calculadora inexistente.
    if (capability.capabilityId === 'calculator_codm_unsupported') {
      transition(FLOW_STATES.INTENT_CONFIRMED, { capabilityId });
      transition(FLOW_STATES.ENTITLEMENT_GUARD);
      transition(FLOW_STATES.UNSUPPORTED_SKILL);
      gphSay(GPH_COPY.codmUnavailable);
      suggestNextSteps(capabilityId);
      return;
    }

    transition(FLOW_STATES.INTENT_CONFIRMED, { capabilityId });
    if (capability.requiresPlan === 'admin' || capability.requiresPlan === 'partner') {
      transition(FLOW_STATES.ADMIN_GUARD);
    } else if (capability.requiresPlan === 'pro_or_legacy') {
      transition(FLOW_STATES.LEGACY_ENTITLEMENT_CHECK);
    } else {
      transition(FLOW_STATES.ENTITLEMENT_GUARD);
    }

    const access = evaluateAccess(capability, {
      user: host.user,
      entitlements: host.entitlements,
      usage: agentStateRef.current.usage,
      hasAdminAccess: host.hasAdminPanelAccess,
      isPartner: host.isPartnerUser,
      hasPrivilegedAccess: host.hasPrivilegedAccess
    });

    if (!access.allowed) {
      if (access.reason === ACCESS_REASONS.FREE_LIMIT_REACHED) {
        transition(FLOW_STATES.FREE_LIMIT_REACHED);
        gphSay(GPH_COPY.freeLimitReached);
        transition(FLOW_STATES.PURCHASE_REQUIRED);
        askChips(null, [
          { label: GPH_COPY.subscribePro, value: 'purchase_pro' },
          { label: GPH_COPY.seeAnnualPlan, value: 'purchase_pro' },
          { label: GPH_COPY.startAnotherProblem, value: 'restart' }
        ], 'next_steps');
        return;
      }
      if (access.reason === ACCESS_REASONS.PRO_REQUIRED) {
        transition(FLOW_STATES.PURCHASE_REQUIRED);
        gphSay(capability.unsupportedFallback || GPH_COPY.proOnlySkill);
        appendMessages({
          role: 'gph',
          kind: 'rich',
          adapterKey: 'PurchaseProAdapter',
          capabilityId: 'purchase_pro',
          context: { blockedReason: GPH_COPY.proOnlySkill },
          text: 'Assinar Pro'
        });
        return;
      }
      if (access.reason === ACCESS_REASONS.ADMIN_DENIED || access.reason === ACCESS_REASONS.PARTNER_DENIED) {
        transition(FLOW_STATES.SUGGESTING_NEXT_STEP);
        gphSay(GPH_COPY.restrictedArea);
        askChips(null, nextStepChips(['profile_plan', 'profile_settings', 'support_community']), 'next_steps');
        return;
      }
      gphSay(GPH_COPY.errorRecovery);
      return;
    }

    // Free consumindo teste da skill (bloqueio por skill, nunca global).
    if (capability.requiresPlan === 'free_limited' && access.reason === ACCESS_REASONS.OK && context.countUsage !== false) {
      registerUsage(capability.capabilityId);
    }

    mountCapability(capability, context);
  };

  // ── Fluxos de pergunta (gate de contexto) ────────────────────────────────
  const askGameForCalculator = (extraContext = {}) => {
    transition(FLOW_STATES.NEEDS_CLARIFICATION);
    transition(FLOW_STATES.ASKING_QUESTION);
    askChips('Para qual jogo você quer calcular as miras?', GAME_OPTIONS, 'game_select', { extraContext });
  };

  const askSensitivitySymptom = () => {
    const capability = CAPABILITIES.sensitivity_diagnostic;
    transition(FLOW_STATES.NEEDS_CLARIFICATION);
    transition(FLOW_STATES.ASKING_QUESTION);
    askChips(
      capability.clarifyingQuestion.question,
      capability.clarifyingQuestion.options,
      'clarify_symptom'
    );
  };

  const askDrillGoal = () => {
    const capability = CAPABILITIES.training_single_drill;
    transition(FLOW_STATES.NEEDS_CLARIFICATION);
    transition(FLOW_STATES.ASKING_QUESTION);
    askChips(
      capability.clarifyingQuestion.question,
      capability.clarifyingQuestion.options,
      'drill_goal'
    );
  };

  const askVideoGoal = (hasVideo = false) => {
    transition(FLOW_STATES.NEEDS_CLARIFICATION);
    transition(FLOW_STATES.ASKING_QUESTION);
    askChips(
      hasVideo ? GPH_COPY.videoNoContext : 'O que você quer descobrir com esse vídeo?',
      VIDEO_GOAL_OPTIONS,
      'video_goal',
      { extraContext: { hasVideo } }
    );
  };

  // ── Expert Core: pesquisa grounded (decisão local + backend revalida) ────
  // O aviso só aparece quando a resposta DEPENDE de meta/patch e a pesquisa
  // não retornou evidência real — nunca fingimos que pesquisamos.
  const maybeResearch = (rawText, naturalDecision) => {
    const lower = String(rawText || '').toLowerCase();
    const decision = evaluateResearch(lower, { intent: naturalDecision?.intent || null });
    logAgentTurn({ intent: naturalDecision?.intent, researchDecision: decision });
    if (!decision.shouldResearch) return;

    const game = naturalDecision?.game
      || conversationCtxRef.current?.playerMemory?.mainGame
      || host.canonicalGame
      || 'pubg_mobile';
    const metaDependent = decision.reason === 'depends_on_current_meta_or_patch';

    showThinking("Consultando contexto...", "Pesquisando apoio");
    requestResearch({
      game,
      intent: 'training_request',
      objective: rawText,
      queryType: decision.queryType
    }).then((answer) => {
      setThinking(false);
      logAgentTurn({
        researchDecision: decision,
        sources: answer.sources?.length || 0,
        evidenceScore: answer.overallConfidence,
        guardApplied: 'truth_policy'
      });
      if (answer.supportMaterial?.url) {
        appendMessages({
          role: 'gph',
          kind: 'text',
          text: `Material de apoio real para esse treino: "${answer.supportMaterial.title}" (canal ${answer.supportMaterial.channel}) — ${answer.supportMaterial.url}`
        });
        return;
      }
      if (answer.researched && answer.synthesis) {
        appendMessages({ role: 'gph', kind: 'text', text: answer.synthesis.slice(0, 600) });
        return;
      }
      if (metaDependent) {
        appendMessages({ role: 'gph', kind: 'notice', text: GPH_COPY.researchUnavailable });
      }
    }).catch(() => {
      setThinking(false);
      if (metaDependent) {
        appendMessages({ role: 'gph', kind: 'notice', text: GPH_COPY.researchUnavailable });
      }
    });
  };

  const applyMiSkillTurn = (skillTurn) => {
    if (!skillTurn?.handled) return false;

    if (skillTurn.pendingContext) {
      patchContext((prev) => ({ ...prev, ...skillTurn.pendingContext }));
    }

    // Bloqueio comercial vindo do backend (source of truth): paywall, sem skill.
    if (skillTurn.status === 'locked') {
      if (skillTurn.sourceStatus === 'free_trial_used') miTrialConsumedRef.current = true;
      showMiPaywall(skillTurn.message);
      if (skillTurn.log) logAgentTurn(skillTurn.log);
      return true;
    }

    // Degustação Free consumida neste turno: a resposta sai normalmente e o
    // próximo turno será bloqueado — registra localmente para refletir já.
    if (skillTurn.semanticDecision?.freeTrial?.consumed) {
      miTrialConsumedRef.current = true;
    }

    if (skillTurn.status === 'unsupported') {
      transition(FLOW_STATES.UNSUPPORTED_SKILL);
      gphSay(sanitizeMiUserFacingText(skillTurn.message, GPH_COPY.codmUnavailable));
      suggestNextSteps(skillTurn.nextStepsCapabilityId || skillTurn.capabilityId);
      return true;
    }

    if (skillTurn.status === 'needs_slots' || skillTurn.status === 'invalid') {
      transition(FLOW_STATES.NEEDS_CLARIFICATION);
      transition(FLOW_STATES.ASKING_QUESTION);
      gphSay(composeMiSkillAnswer({ ...skillTurn, fallback: GPH_COPY.missingContext }));
      return true;
    }

    if (skillTurn.status === 'error') {
      transition(FLOW_STATES.ERROR_RECOVERY);
      gphSay(composeMiSkillAnswer({ ...skillTurn, fallback: GPH_COPY.errorRecovery }));
      return true;
    }

    if (skillTurn.status === 'success') {
      transition(FLOW_STATES.SHOWING_RESULT);
      appendMessages({
        role: 'gph',
        kind: 'text',
        capabilityId: skillTurn.capabilityId,
        text: composeMiSkillAnswer({ ...skillTurn, fallback: GPH_COPY.generalHelpFps })
      });
      if (skillTurn.contextPatch?.capabilityId) {
        patchContext((prev) => updateFromResult(
          prev,
          skillTurn.contextPatch.capabilityId,
          skillTurn.contextPatch.data || {}
        ));
      }
      if (skillTurn.log) {
        logAgentTurn(skillTurn.log);
      }
      suggestNextSteps(skillTurn.nextStepsCapabilityId || skillTurn.capabilityId);
      return true;
    }

    return false;
  };

  // Executa a decisão do agente de conversa natural. Turn discipline: cada
  // mensagem do usuário gera exatamente UMA saída principal — nunca chat +
  // pricing + settings empilhados.
  const applyNaturalDecision = (decision, rawText = '') => {
    // Slot filling: persiste (ou limpa) o que a Mi perguntou para interpretar
    // a próxima resposta livre ("sim, full" responde o gyro).
    patchContext((prev) => ({
      ...prev,
      pendingSkill: decision.pending?.skill || null,
      pendingSlots: decision.pending?.missingSlots || [],
      slots: decision.pending?.slots || {},
      lastUserText: rawText || prev.lastUserText || null,
      lastAssistantText: decision.reply || prev.lastAssistantText || null
    }));
    if (decision.memoryPatch) {
      patchContext((prev) => updatePlayerMemory(prev, decision.memoryPatch));
    }

    const action = selectSinglePrimaryOutput(decision);

    if (action === 'chat_response' || action === 'redirect_out_of_scope') {
      gphSay(sanitizeMiUserFacingText(decision.reply, GPH_COPY.generalHelpFps));
      transition(FLOW_STATES.IDLE);
      return;
    }

    if (action === 'ask_slot' || action === 'ask_video') {
      transition(FLOW_STATES.NEEDS_CLARIFICATION);
      transition(FLOW_STATES.ASKING_QUESTION);
      gphSay(sanitizeMiUserFacingText(decision.reply, GPH_COPY.missingContext));
      return;
    }

    if (action === 'render_settings') { guardAndMount('profile_settings', {}); return; }
    if (action === 'render_pricing') { guardAndMount('purchase_pro', {}); return; }
    if (action === 'render_limits') { guardAndMount('profile_plan', {}); return; }
    if (action === 'render_support') { guardAndMount('support_community', {}); return; }

    if ((action === 'execute_skill' || action === 'render_result_module') && decision.capabilityId) {
      // Voz única da Mi: capabilities do tipo 'skill' já têm a resposta final
      // composta pelo miAnswerComposer no mount — falar o reply aqui geraria
      // mensagem duplicada ("Recebi seu vídeo..." duas vezes).
      const capability = getCapability(decision.capabilityId);
      const composerWillSpeak = capability?.type === 'skill';
      if (decision.reply && !composerWillSpeak) {
        gphSay(sanitizeMiUserFacingText(decision.reply, GPH_COPY.generalHelpFps));
      }
      guardAndMount(decision.capabilityId, decision.mountContext || {});
      if (decision.skill === 'training' && rawText) maybeResearch(rawText, decision);
      return;
    }

    gphSay(decision.reply || GPH_COPY.fpsClarify);
    transition(FLOW_STATES.IDLE);
  };

  // ── Entrada do usuário ───────────────────────────────────────────────────
  const handleUserText = (text) => {
    if (inputBlocked) return;
    // Free bloqueado: paywall imediato, sem chamada de backend/Gemini/skill.
    if (miTrialLocked) {
      appendMessages({ role: 'user', kind: 'text', text });
      showMiPaywall('Sua conversa de degustação com a Mi já foi usada. Assine o Pro para conversar com a Mi e liberar todas as ferramentas.');
      return;
    }
    appendMessages({ role: 'user', kind: 'text', text, persist: false });
    transition(FLOW_STATES.INPUT_RECEIVED);
    transition(FLOW_STATES.CONTEXT_CHECKING);

    let actMsg = "Analisando seu pedido...";
    const lower = text.toLowerCase();
    if (lower.includes("sensibilidade") || lower.includes("sensi") || lower.includes("mira")) {
      actMsg = "Calibrando dispositivo...";
    } else if (lower.includes("treino") || lower.includes("rotina") || lower.includes("jogada")) {
      actMsg = "Gerando treino...";
    } else if (lower.includes("video") || lower.includes("gameplay") || lower.includes("replay")) {
      actMsg = "Validando vídeo...";
    } else if (lower.includes("calcular") || lower.includes("calculadora") || lower.includes("scope")) {
      actMsg = "Calculando miras...";
    }

    respondWith(() => handleUserTextResolved(text), actMsg);
  };

  const handleUserTextResolved = async (text) => {
    // Pergunta pendente de texto livre do fluxo de chips (ex.: "qual jogada?").
    if (flow.pendingQuestion?.type === 'drill_free_text') {
      transition(FLOW_STATES.INTENT_CONFIRMED, { pendingQuestion: null });
      guardAndMount('training_single_drill', { drillGoal: text, rawText: text });
      return;
    }

    // Conversation State Manager: troca explícita de objetivo ("vamos mudar",
    // "começar outro problema") encerra pendências e rebaixa resultados
    // anteriores ANTES de interpretar — contexto velho não contamina o novo.
    const turnKind = classifyTurn({ text, hasAttachment: false, ctx: conversationCtxRef.current });
    if (turnKind === TURN_KINDS.CONTEXT_RESET) {
      const resetCtx = applyContextReset(conversationCtxRef.current);
      conversationCtxRef.current = resetCtx;
      patchContext(() => resetCtx);
      logAgentTurn({ intent: 'context_reset', schemaType: 'conversation_state_reset' });
    }

    const backendTurn = await sendMiConversationTurn({
      conversationId: conversationIdRef.current,
      text,
      conversationContext: conversationCtxRef.current,
      host,
      language: 'pt-BR'
    });

    if (backendTurn.ok && backendTurn.turn?.handled) {
      const turn = backendTurn.turn;
      if (turn.freeTrial?.consumed) {
        miTrialConsumedRef.current = true;
      }
      if (turn.conversationContextPatch) {
        patchContext((prev) => ({ ...prev, ...turn.conversationContextPatch }));
      }
      if (turn.mode === 'locked') {
        showMiPaywall(turn.message);
        return;
      }
      if (turn.mode === 'answer' || turn.mode === 'clarify' || turn.mode === 'unsupported') {
        transition(turn.mode === 'clarify' ? FLOW_STATES.ASKING_QUESTION : FLOW_STATES.IDLE);
        gphSay(sanitizeMiUserFacingText(turn.message, GPH_COPY.generalHelpFps), { persist: false });
        return;
      }
      if (turn.mode === 'skill' && turn.semanticDecision) {
        const skillTurn = await runMiSkillTurn({
          text,
          conversationContext: {
            ...conversationCtxRef.current,
            ...(turn.conversationContextPatch || {})
          },
          host,
          language: 'pt',
          semanticDecisionOverride: turn.semanticDecision,
          onRunning: ({ capabilityId, mountedAdapter, message }) => {
            transition(FLOW_STATES.INTENT_CONFIRMED, { capabilityId });
            transition(FLOW_STATES.COLLECTING_SKILL_INPUTS);
            transition(FLOW_STATES.MOUNTING_SKILL, { capabilityId, mountedAdapter });
            transition(FLOW_STATES.RUNNING_SKILL);
            showThinking(message || "Executando skill...", "Executando skill");
          }
        });
        if (skillTurn?.handled) {
          applyMiSkillTurn(skillTurn);
          return;
        }
      }
    } else if (uid && conversationIdRef.current) {
      persistMessage(uid, conversationIdRef.current, { role: 'user', type: 'text', text });
    }

    const skillTurn = await runMiSkillTurn({
      text,
      conversationContext: conversationCtxRef.current,
      host,
      language: 'pt',
      onRunning: ({ capabilityId, mountedAdapter, message }) => {
        transition(FLOW_STATES.INTENT_CONFIRMED, { capabilityId });
        transition(FLOW_STATES.COLLECTING_SKILL_INPUTS);
        transition(FLOW_STATES.MOUNTING_SKILL, { capabilityId, mountedAdapter });
        transition(FLOW_STATES.RUNNING_SKILL);
        showThinking(message || "Executando skill...", "Executando skill");
      }
    });

    // Degustação consumida (decidido pelo backend): vale também quando a
    // resposta sai pela camada de conversa local.
    if (skillTurn?.semanticDecision?.freeTrial?.consumed) {
      miTrialConsumedRef.current = true;
    }

    if (skillTurn?.handled) {
      applyMiSkillTurn(skillTurn);
      return;
    }

    // Agente de conversa natural: entende texto livre, conduz slot filling
    // por pergunta natural e só executa skill quando os dados estão completos.
    // A decisão da Mi Brain viaja como dica semântica — quando o texto cairia
    // no genérico, a resposta contextual do cérebro tem prioridade.
    // A Mi sabe que existe vídeo recente na conversa: "avalie minha
    // movimentação" depois de um upload usa o vídeo existente em vez de
    // pedir outro do zero.
    const decision = understand(text, conversationCtxRef.current, {
      hasVideo: hasRecentVideo(conversationCtxRef.current, host),
      hostGame: host.canonicalGame,
      semanticHint: skillTurn?.semanticDecision || null
    });
    logAgentTurn({
      intent: decision.intent,
      confidence: decision.confidence,
      slots: decision.missingSlots,
      skillChosen: decision.capabilityId
    });
    applyNaturalDecision(decision, text);
  };

  const handleAttachVideo = (file) => {
    if (inputBlocked || !file) return;
    // Análise de vídeo é Pro: bloqueia ANTES de qualquer upload/processamento
    // pesado (o backend também bloqueia pré-upload — dupla proteção).
    if (!isProUser) {
      appendMessages({ role: 'user', kind: 'text', text: `Vídeo selecionado: ${file.name}` });
      showMiPaywall('A análise de vídeo é exclusiva do plano Pro. Assine o Pro para a Mi analisar sua gameplay.');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      gphSay('Esse vídeo passa de 200MB. Envie um arquivo menor para eu conseguir analisar.');
      return;
    }
    host.selectVideo?.(file);
    appendMessages({ role: 'user', kind: 'text', text: `Vídeo enviado: ${file.name}` });
    transition(FLOW_STATES.INPUT_RECEIVED);
    transition(FLOW_STATES.CONTEXT_CHECKING);
    respondWith(async () => {
      // Conversation State Manager: registra o vídeo e decide a qual objetivo
      // ele pertence — pendência de análise ativa, validação de diagnóstico/
      // treino DESTA conversa, ou nenhum (perguntar o foco). Contexto velho
      // reidratado (ex.: "treino: cover" de outra sessão) nunca captura o vídeo.
      const ctxWithVideo = registerVideoAttachment(conversationCtxRef.current, file);
      conversationCtxRef.current = ctxWithVideo;
      patchContext(() => ctxWithVideo);

      const objective = resolveVideoObjective({ ctx: ctxWithVideo });

      if (objective.kind === 'ask_goal') {
        // Sem objetivo ativo: uma única pergunta de foco, e a pendência passa
        // a ser da Mi Análise — a próxima resposta ("movimentação") executa.
        const askCtx = {
          ...ctxWithVideo,
          pendingSkill: 'mi_analysis',
          pendingSlots: ['analysisGoal'],
          slots: {}
        };
        conversationCtxRef.current = askCtx;
        patchContext(() => askCtx);
        transition(FLOW_STATES.NEEDS_CLARIFICATION);
        transition(FLOW_STATES.ASKING_QUESTION);
        gphSay(GPH_COPY.videoReceivedNoContext);
        logAgentTurn({ intent: 'video_attachment', schemaType: 'video_goal_question' });
        return;
      }

      try {
        const skillTurn = await runMiSkillTurn({
          text: `Video enviado: ${file.name}`,
          conversationContext: {
            ...ctxWithVideo,
            pendingSkill: 'mi_analysis',
            pendingSlots: [],
            slots: {
              ...(ctxWithVideo.slots || {}),
              analysisGoal: objective.analysisGoal,
              video: file,
              ...(objective.notes ? { notes: objective.notes } : {})
            }
          },
          host: {
            ...host,
            selectedVideo: file
          },
          language: 'pt',
          onRunning: ({ capabilityId, mountedAdapter, message }) => {
            transition(FLOW_STATES.INTENT_CONFIRMED, { capabilityId });
            transition(FLOW_STATES.COLLECTING_SKILL_INPUTS);
            transition(FLOW_STATES.MOUNTING_SKILL, { capabilityId, mountedAdapter });
            transition(FLOW_STATES.RUNNING_SKILL);
            showThinking(message || "Executando skill...", "Executando skill");
          }
        });
        if (skillTurn?.handled && applyMiSkillTurn(skillTurn)) return;
      } catch (error) {
        console.error('[GPH_AGENT] Video skill turn failed', error);
      }

      // Fallback (orquestrador indisponível): decisão local com o mesmo
      // contexto do vídeo — nunca herda objetivo de sessão antiga.
      const decision = understandVideoAttachment(conversationCtxRef.current, {
        hostGame: host.canonicalGame
      });
      applyNaturalDecision(decision);
    });
  };

  // ── Chips ────────────────────────────────────────────────────────────────
  const restart = () => {
    transition(FLOW_STATES.IDLE, { capabilityId: null, mountedAdapter: null, pendingQuestion: null });
    gphSay('Fechou. Me diga o que você quer melhorar agora.');
  };

  const startNewConversation = () => {
    const convId = newConversationId();
    conversationIdRef.current = convId;
    conversationBytesRef.current = 0;
    setInputBlocked(false);
    setMessages([{ id: nextId(), role: 'gph', kind: 'text', text: GPH_COPY.welcome }]);
    dispatchFlow({ type: 'RESET' });
    // Limpa o contexto DA CONVERSA, preservando a memória permanente do
    // jogador (jogo principal, controle, device, estilo).
    patchContext((prev) => resetConversationContext(prev));
    if (uid) saveAgentState(uid, { activeConversationId: convId, plan });
  };

  const selectChip = (message, chip) => {
    if (message.resolved) return;
    resolveChipMessage(message.id, chip.value);
    appendMessages({ role: 'user', kind: 'text', text: chip.label });

    const kind = message.chipsKind;
    const extra = message.extraContext || {};

    if (kind === 'conversation_limit') {
      startNewConversation();
      return;
    }

    transition(FLOW_STATES.INPUT_RECEIVED);
    transition(FLOW_STATES.CONTEXT_CHECKING);

    let actMsg = "Analisando seu pedido...";
    if (kind === 'clarify_symptom') {
      actMsg = "Calibrando dispositivo...";
    } else if (kind === 'drill_goal') {
      actMsg = "Gerando treino...";
    } else if (kind === 'video_goal') {
      actMsg = "Validando vídeo...";
    } else if (kind === 'game_select') {
      actMsg = "Calculando miras...";
    }

    respondWith(() => selectChipResolved(kind, chip, extra), actMsg);
  };

  const selectChipResolved = (kind, chip, extra) => {
    if (kind === 'clarify_symptom') {
      guardAndMount('sensitivity_diagnostic', { symptom: chip.value });
      return;
    }

    if (kind === 'drill_goal') {
      if (chip.value === 'outra') {
        transition(FLOW_STATES.NEEDS_CLARIFICATION);
        transition(FLOW_STATES.ASKING_QUESTION, { pendingQuestion: { type: 'drill_free_text' } });
        gphSay('Me diga a jogada ou situação que você quer treinar.');
        return;
      }
      guardAndMount('training_single_drill', { drillGoal: chip.value });
      return;
    }

    if (kind === 'video_goal') {
      guardAndMount(chip.value, { hasVideo: Boolean(extra.hasVideo) });
      return;
    }

    if (kind === 'calc_ambiguity') {
      if (chip.value === 'has_base') {
        askGameForCalculator({ baseValue: extra.baseValue });
      } else {
        askSensitivitySymptom();
      }
      return;
    }

    if (kind === 'raw_value') {
      if (chip.value === 'calc_others') {
        askGameForCalculator({ baseValue: extra.baseValue });
      } else if (chip.value === 'validate_video') {
        askVideoGoal(false);
      } else if (chip.value === 'save_config') {
        gphSay('Boa. Guarde esse valor como referência — posso abrir suas configurações para você registrar.');
        guardAndMount('profile_settings', {});
      } else {
        askSensitivitySymptom();
      }
      return;
    }

    if (kind === 'game_select') {
      if (chip.value === 'codm') {
        guardAndMount('calculator_codm_unsupported', {});
        return;
      }
      const capability = resolveCalculatorForGame(chip.value);
      if (capability) guardAndMount(capability.capabilityId, { game: chip.value, baseValue: extra.baseValue });
      return;
    }

    if (kind === 'confirm_intent') {
      if (chip.value.startsWith('confirm:')) {
        guardAndMount(chip.value.slice('confirm:'.length), {});
      } else {
        transition(FLOW_STATES.IDLE);
        gphSay('Sem problema. Me diga com suas palavras o que você quer melhorar.');
      }
      return;
    }

    // next_steps / fallback
    if (chip.value === 'restart') {
      restart();
      return;
    }
    if (chip.value === 'calculator_auto') {
      const capability = resolveCalculatorForGame(host.canonicalGame);
      if (capability) {
        guardAndMount(capability.capabilityId, { game: host.canonicalGame });
      } else {
        askGameForCalculator({});
      }
      return;
    }
    if (chip.value.startsWith('mi_analysis')) {
      guardAndMount(chip.value, {});
      return;
    }
    if (getCapability(chip.value)) {
      guardAndMount(chip.value, {});
    }
  };

  // ── API entregue aos adapters ────────────────────────────────────────────
  const agent = {
    host,
    copy: GPH_COPY,
    planLabel,
    usage: agentState.usage,
    favorites: favoritesApi,
    selectChip,
    restart,
    runCapability: (capabilityId, context = {}) => guardAndMount(capabilityId, context),
    deliverDrill: (drillId) => guardAndMount('training_single_drill', { drillId, countUsage: false }),
    askAnotherDrill: askDrillGoal,
    completeSkill: (capabilityId) => suggestNextSteps(capabilityId || flow.capabilityId),
    startNewConversation,
    // Expert Core: contexto da conversa acessível aos adapters.
    context: conversationCtx,
    updateContext: (key, data) => patchContext((prev) => ({ ...prev, [key]: data })),
    updatePlayerMemory: (patch) => patchContext((prev) => updatePlayerMemory(prev, patch))
  };

  // Quick actions matam a experiência de conversa — só existem atrás da flag
  // de debug GPH_QUICK_ACTIONS_ENABLED (default: desligada em produção).
  const showQuickIntents = useMemo(() => {
    // Mi-first: a conversa livre e a interface principal. Quick actions ficam
    // fora da timeline para nao criar loops nem substituir a resposta da Mi.
    return false;
  }, []);

  return (
    <div className="gph-agent">
      <MiIonicAppShell
        topBar={(
          <MiTopBar
            planLabel={planLabel}
            onOpenMenu={() => setOverflowOpen(true)}
          />
        )}
        footer={(
          <div className="gph-agent__bottom">
            {showQuickIntents && <GphQuickIntents onSelect={handleUserText} disabled={inputBlocked || thinking} />}
            <GphAgentInput
              onSend={handleUserText}
              onAttachVideo={handleAttachVideo}
              disabled={inputBlocked || thinking}
              blockedNotice={inputBlocked ? (plan === 'pro' ? GPH_COPY.conversationLimitPro : GPH_COPY.conversationLimitFree) : null}
            />
          </div>
        )}
        overlays={(
          <MiOverflowMenu
            isOpen={overflowOpen}
            onClose={() => setOverflowOpen(false)}
            onSelectCapability={(capabilityId) => guardAndMount(capabilityId, {})}
            onSignOut={host.signOut}
            hasAdminPanelAccess={host.hasAdminPanelAccess}
            isPartnerUser={host.isPartnerUser}
          />
        )}
      >
        <GphConversationCanvas messages={messages} thinking={thinking} agent={agent} />
      </MiIonicAppShell>
    </div>
  );
}
